import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import MDAccordion from "components/MDAccordian";
import MDButton from "components/MDButton";
import React, { useEffect, useRef, useState } from "react";
import ParameterForm from "./parameterForm";
import RequiredFieldForm from "./requiredFieldForm";
import ProcessingRulesForm from "./processingRulesForm";
import CrudService from "services/cruds-service";
import MDModal from "components/MDModel";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import CloseIcon from "@mui/icons-material/Close";
import MDInput from "components/MDInput";
import { toast } from "react-toastify";
import MDDatePicker from "components/MDDatePicker";
import FilterForm from "./filterForm";

function JobFormTesting({
  selectedCompanyRows,
  filterData,
  onJobTypeSelect,
  onSocketDataRemove,
  onProgressReset,
  onCompletedJobDataRemove,
  jobStatus,
  setJobStatus,
  isBatch,
  children,
}) {
  const [data, setData] = useState([]);
  const [jobTypeId, setJobTypeId] = useState([]);
  const [openModel, setOpenModel] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(null);
  const [customBatch, setCustomBatch] = useState([]);
  const [customBatchDetails, setCustomBatchDetails] = useState({
    buttonName: "Add",
    showFields: false,
    name: "",
    id: null,
    description: "",
  });
  const duration = [
    { key: "30", name: "30 Second" },
    { key: "60", name: "1 Minute" },
    { key: "120", name: "2 Minute" },
    { key: "300", name: "5 Minute" },
    { key: "600", name: "10 Minute" },
  ];
  const [selectCustomBatch, setSelectCustomBatch] = useState(null);
  const [concurrency, setConcurrency] = useState(
    () => JSON.parse(localStorage.getItem("concurrency")) || 20
  );
  const [debouncedConcurrency, setDebouncedConcurrency] = useState(concurrency);
  const [limiterMax, setLimiterMax] = useState(
    () => JSON.parse(localStorage.getItem("limiterMax")) || 20
  );
  const [debouncedLimiterMax, setDebouncedLimiterMax] = useState(limiterMax);
  const [limiterDuration, setLimiterDuration] = useState(
    () => JSON.parse(localStorage.getItem("limiterDuration")) || 60
  );
  const [debouncedLimiterDuration, setDebouncedLimiterDuration] = useState(limiterDuration);
  const jobDataRef = useRef({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [loader, setLoader] = useState(false)

  useEffect(() => {
    localStorage.setItem("concurrency", JSON.stringify(concurrency));
    localStorage.setItem("limiterMax", JSON.stringify(limiterMax));
    localStorage.setItem("limiterDuration", JSON.stringify(limiterDuration));
  }, [concurrency, limiterMax, limiterDuration]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedConcurrency(concurrency);
      setDebouncedLimiterMax(limiterMax);
      setDebouncedLimiterDuration(limiterDuration);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [concurrency, limiterMax, limiterDuration]);

  useEffect(() => {
    (async () => {
      try {
        const response = await CrudService.getJobTypes();
        setData(response.data);
      } catch (err) {
        console.error("err", err);
      }
    })();
    (async () => {
      try {
        const response = await CrudService.getBatchJobs();
        setCustomBatch(response.data);
      } catch (err) {
        console.error("err", err);
      }
    })();
  }, []);

  useEffect(() => {
    if (debouncedConcurrency !== undefined && debouncedLimiterMax !== undefined && debouncedLimiterDuration !== undefined) {
      setConcurrencyOption();
    }
  }, [debouncedConcurrency, debouncedLimiterMax, debouncedLimiterDuration]);

  const setConcurrencyOption = async () => {
    const apiData = {
      concurrency: Number(debouncedConcurrency),
      limiter: {
        max: Number(debouncedLimiterMax),
        duration: Number(debouncedLimiterDuration) * 1000,
      },
    };
    try {
      await CrudService.setConcurrencyOption(apiData);
    } catch (err) {
      console.error("Processing error:", err);
    }
  };

  const selectCustomJobsBatch = (value) => {
    const selectedBatch = customBatch.find((e) => e.batch_id === value);
    if (selectedBatch) {
      setCustomBatchDetails({
        buttonName: "Update",
        showFields: false,
        name: selectedBatch.batch_name,
        id: selectedBatch.batch_id,
        description: selectedBatch.description || "",
      });
    }

    setSelectCustomBatch(value);
    if (value == "none") {
      setCustomBatchDetails({
        buttonName: "Add",
        showFields: false,
        name: "",
        id: null,
        description: "",
      });
      setJobTypeId([]);
      jobDataRef.current = {};
      return;
    }
    const selectedJobTypes = customBatch.filter((e) => e.batch_id == value)[0]
      .JobTypeConfigurations;
    const updatedJobData = selectedJobTypes.reduce((acc, job, index) => {
      const jobDetails = data.find(
        (jobs) => jobs.job_type_id === job.job_type_id
      );
      acc[job.job_type_id] = {
        order_no: index + 1,
        typeId: job.job_type_id,
        job_name: jobDetails.job_name,
        parameters: job.parameters,
        validations: job.validations || null,
        processing_rules: job.processing_rules,
        filters: job.filters,
        destination_url: jobDetails.destination_url,
      };
      return acc;
    }, {});
    const jobIds = selectedJobTypes.map((e) => e.job_type_id);
    setJobTypeId(() => [...jobIds]);
    jobDataRef.current = updatedJobData;
  };

  const selectJobType = (values) => {
    setJobTypeId(values);

    const selectedJobTypes = values
      .map((jobId) => data.find((e) => e.job_type_id === jobId))
      .filter(Boolean);

    const updatedJobData = selectedJobTypes.reduce((acc, job, index) => {
      acc[job.job_type_id] = {
        order_no: index + 1,
        typeId: job.job_type_id,
        job_name: job.job_name,
        parameters: job.parameters,
        validations: job.validations || null,
        processing_rules: job.processing_rules,
        filters: job.filters,
        destination_url: job.destination_url,
      };
      return acc;
    }, {});

    jobDataRef.current = updatedJobData;
  };

  const updateJobData = (jobId, property, value) => {
    setJobTypeId((prev) => prev);
    const updatedJobData = {
      ...jobDataRef.current,
      [jobId]: { ...jobDataRef.current[jobId], [property]: value },
    };
    jobDataRef.current = updatedJobData;
  };

  const initialState = (data) => {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key,
        Array.isArray(value.options) ? value.default : value,
      ])
    );
  };

  async function runJob() {
    if (!selectedCompanyRows.length) {
      alert("Please select records");
      return;
    }
    setLoader(true)

    const filteredCompanies = filterData.filter((company) =>
      selectedCompanyRows.includes(company.company_id)
    );

    if (!selectedDate) {
      setJobStatus(true);
    }
    onJobTypeSelect(jobTypeId);
    onSocketDataRemove([]);
    onProgressReset({});
    onCompletedJobDataRemove({});
    setOpenModel(false);
    const jobs = Object.entries(jobDataRef.current).map(([id, value]) => {
      const updatedFilters = value.filters
        ? Object.keys(value.filters).reduce((acc, key) => {
          acc[key] = {
            ...value.filters[key],
            filters: value.filters[key].filters.map((filter) => {
              if (filter.condition === "LIKE") {
                return {
                  ...filter,
                  value: `%${filter.value}%`,
                };
              }
              return filter;
            }),
          };
          return acc;
        }, {})
        : value.filters;

      return {
        id,
        order_no: value.order_no,
        requiredState: value?.validations?.missing_value_action?.fail,
        validations: value.parameters
          ? initialState(value.parameters)
          : value.parameters,
        processingState: value.processing_rules,
        filters: updatedFilters,
      };
    });

    try {
      let response = {};

      if (isBatch) {
        response = await CrudService.runBatch({
          jobs: jobs.sort((a, b) => a.order_no - b.order_no),
          campaigns: filteredCompanies.map((company) => company.uuid),
          scheduleAt: selectedDate,
        });
      } else {
        response = await CrudService.multipleJob({
          jobs: jobs.sort((a, b) => a.order_no - b.order_no),
          campaigns: filteredCompanies.map((company) => company.uuid),
          scheduleAt: selectedDate,
        });
      }
      if (selectedDate) {
        toast.info(response.message);
        setCustomBatchDetails({
          buttonName: "Add",
          showFields: false,
          name: "",
          id: null,
          description: "",
        });
        setJobTypeId([]);
        setCurrentBatch(null);
        setSelectCustomBatch(null);
        setSelectedDate(null);
        jobDataRef.current = {};
      }
      setCurrentBatch(response.batch_id);
    } catch (err) {
      console.error("Processing error:", err);
    }
    setLoader(false)
  }

  const stopJob = async () => {
    setLoader(true)
    try {
      const response = await CrudService.stopJob(currentBatch);
      if (response.success) {
        setJobStatus(false);
      }
    } catch (err) {
      console.error("Processing error:", err);
    }
    setLoader(false)
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(jobTypeId);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setJobTypeId(items);

    const updatedJobData = { ...jobDataRef.current };
    items.forEach((id, index) => {
      if (updatedJobData[id]) updatedJobData[id].order_no = index + 1;
    });

    jobDataRef.current = updatedJobData;
  };

  const handleRemoveItem = (typeId) => {
    setJobTypeId((prev) => {
      const updatedJobTypeId = prev.filter((id) => id !== typeId);
      const updatedJobData = { ...jobDataRef.current };
      delete updatedJobData[typeId];
      updatedJobTypeId.forEach(
        (id, idx) => (updatedJobData[id].order_no = idx + 1)
      );
      jobDataRef.current = updatedJobData;
      return updatedJobTypeId;
    });
  };

  const updateBatchDetails = (key, value) => {
    setCustomBatchDetails((prev) => ({ ...prev, [key]: value }));
  };

  const deleteJobBatch = async () => {
    const payload = {
      is_deleted: true,
      batch_id: customBatchDetails.id,
    };
    try {
      let conf = confirm("Are you sure you want to delete?");
      if (!conf) {
        return;
      }
      const response = await CrudService.mutationBatchJobType(payload);
      if (response.success) {
        setCustomBatch((prevCustomBatch) =>
          prevCustomBatch.filter((batch) => batch.batch_id !== payload.batch_id)
        );
        setCustomBatchDetails({
          buttonName: "Add",
          showFields: false,
          name: "",
          id: null,
          description: "",
        });
        setJobTypeId([]);
        setCurrentBatch(null);
        setSelectCustomBatch(null);
        jobDataRef.current = {};
        setOpenModel(false);
        toast.info(response.message);
      }
    } catch (err) {
      console.error("Processing error:", err);
    }
  };

  const handleBatchButtonClick = async (isUpdate = false) => {
    if (!customBatchDetails.name) {
      alert("Custom Batch Name is required");
      return;
    }

    const jobs = Object.entries(jobDataRef.current).map(
      ([id, value], index) => ({
        job_type_id: Number(id),
        config_name: `${customBatchDetails.name}_${index}`,
        order_no: value.order_no,
        parameters: value.parameters,
        validations: value?.validations,
        processing_rules: value.processing_rules,
        filters: value.filters,
        description: "",
      })
    );

    if (jobs.length <= 0) {
      alert("Please select at least one job");
      return;
    }

    try {
      const payload = {
        ...(isUpdate && { batch_id: customBatchDetails.id }),
        batch_name: customBatchDetails.name,
        description: customBatchDetails.description,
        JobTypeConfigurations: jobs,
      };
      const response = await CrudService.mutationBatchJobType(payload);
      if (response.success) {
        const batchIndex = customBatch.findIndex(
          (batch) => batch.batch_id === response.data.batch_id
        );

        if (batchIndex > -1) {
          setCustomBatch((prevCustomBatch) => {
            const newBatchList = [...prevCustomBatch];
            newBatchList[batchIndex] = response.data;
            return newBatchList;
          });
        } else {
          setCustomBatch((prevCustomBatch) => [
            ...prevCustomBatch,
            response.data,
          ]);
        }
        // setCustomBatchDetails({
        //   buttonName: "Add",
        //   showFields: false,
        //   name: "",
        //   id: null,
        //   description: "",
        // });
        // setJobTypeId([]);
        // setCurrentBatch(null);
        // setSelectCustomBatch(null);
        // jobDataRef.current = {};
        setOpenModel(false);
        toast.info(response.message);
      }
    } catch (err) {
      console.error("Processing error:", err);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date[0]);
  };

  return (
    <Box sx={{ pt: 1, pb: 1, width: "100%" }}>
      <Grid container spacing={3} alignItems="center">
        {children}
        <Grid item xs={6}>
          <Box sx={{ display: "flex", gap: 2, pt: 0.5 }}>
            <MDButton
              variant="contained"
              color="info"
              onClick={() => {
                setOpenModel(true);
              }}
            >
              Configure Job Types
            </MDButton>
            <TextField
              label="Concurrency"
              value={concurrency}
              onChange={(e) => setConcurrency(e.target.value)}
            />
            <TextField
              label="Limiter Max (No. of records)"
              value={limiterMax}
              onChange={(e) => setLimiterMax(e.target.value)}
              sx={{
                width: "30%",
              }}
            />
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>Limiter Duration (No. of time)</InputLabel>
                <Select
                  label="Limiter Duration (No. of time)"
                  value={limiterDuration}
                  onChange={(e) => setLimiterDuration(e.target.value)}
                  fullWidth
                  sx={{ height: '45px' }}
                >
                  {duration.map((c, idx) => (
                    <MenuItem key={idx} value={c.key}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {jobTypeId.length > 0 && (
              <>
                {!jobStatus && (
                  <MDButton variant="gradient" color="info" onClick={runJob} disabled={loader}>
                    {loader && <CircularProgress size="30px" sx={{ position: 'absolute' }} />}
                    Run
                  </MDButton>
                )}
                {jobStatus && (
                  <MDButton variant="gradient" color="info" onClick={stopJob} disabled={loader}>
                    {loader && <CircularProgress size="30px" sx={{ position: 'absolute' }} />}
                    Stop
                  </MDButton>
                )}
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Modal for Configuring Job Types */}
      <MDModal
        open={openModel}
        onClose={() => {
          onJobTypeSelect(jobTypeId);
          setOpenModel(false);
        }}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        footer={
          <>
            {!jobStatus && (
              <MDButton onClick={runJob} variant="contained" color="info">
                Run
              </MDButton>
            )}
            {customBatchDetails.showFields && (
              <MDButton
                onClick={() => handleBatchButtonClick(customBatchDetails.id)}
                variant="contained"
                color="info"
              >
                {!customBatchDetails.id ? "Save Batch" : "Update Batch"}
              </MDButton>
            )}
          </>
        }
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          {/* Custom job selector */}
          <FormControl sx={{ marginBottom: "10px" }}>
            <InputLabel>Custom Batch Jobs</InputLabel>
            <Select
              // key={jobTypeId.join("|")}
              value={selectCustomBatch}
              onChange={(e) => selectCustomJobsBatch(e.target.value)}
              label="Custom Batch Jobs"
              sx={{
                height: "45px",
                width: "200px",
                "& .MuiSelect-nativeInput": { height: "45px !important" },
              }}
            >
              <MenuItem key={900} value={"none"}>
                {"None"}
              </MenuItem>
              {customBatch.map((job, idx) => (
                <MenuItem key={idx} value={job.batch_id}>
                  {job.batch_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {customBatchDetails.showFields && (
            <FormControl sx={{ marginBottom: "10px" }}>
              <TextField
                label="Custom Batch Name"
                value={customBatchDetails.name}
                onChange={(e) => updateBatchDetails("name", e.target.value)}
                fullWidth
              />
            </FormControl>
          )}

          {/* Button */}
          {!customBatchDetails.showFields && (
            <FormControl sx={{ marginBottom: "10px" }}>
              <MDButton
                onClick={() =>
                  setCustomBatchDetails((prevDetails) => ({
                    ...prevDetails,
                    showFields: true,
                  }))
                }
                variant="contained"
                color="info"
              >
                {customBatchDetails.buttonName}
              </MDButton>
            </FormControl>
          )}
          {customBatchDetails.id && (
            <FormControl sx={{ marginBottom: "10px" }}>
              <MDButton
                onClick={deleteJobBatch}
                variant="contained"
                color="error"
              >
                Delete
              </MDButton>
            </FormControl>
          )}
        </Box>
        {customBatchDetails.showFields && (
          <FormControl fullWidth sx={{ marginBottom: "10px" }}>
            <TextField
              label="Description"
              value={customBatchDetails.description}
              onChange={(e) =>
                updateBatchDetails("description", e.target.value)
              }
              fullWidth
            />
          </FormControl>
        )}

        <Box display="flex" gap={2}>
          {/* Job Type Selector */}
          <FormControl fullWidth>
            <InputLabel>Job Type</InputLabel>
            <Select
              key={jobTypeId.join("|")}
              value={jobTypeId}
              onChange={(e) => selectJobType(e.target.value)}
              label="Job Type"
              multiple
              sx={{
                height: "45px",
                "& .MuiSelect-select": { height: "45px !important" },
              }}
            >
              {data.map((job, idx) => (
                <MenuItem key={idx} value={job.job_type_id}>
                  {job.job_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <MDDatePicker
            value={selectedDate}
            onChange={handleDateChange}
            options={{
              dateFormat: "Y-m-d H:i",
              enableTime: true,
              minDate: new Date(),
            }}
            input={{ placeholder: "Schedule Time" }}
          />
        </Box>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="job-list">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {jobTypeId.map((id, index) => {
                  // {Object.values(jobDataRef.current).map((job, index) => {
                  // const job = data.find((job) => job.job_type_id === typeId);
                  const job = jobDataRef.current[id];
                  return job ? (
                    <Draggable
                      key={job.typeId}
                      draggableId={job.typeId.toString()}
                      index={index}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <IconButton
                              {...provided.dragHandleProps}
                              sx={{ cursor: "grab" }}
                            >
                              <DragHandleIcon />
                            </IconButton>
                            <Box flexGrow={1} width={100}>
                              <MDAccordion summary={job.job_name}>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: "100", mb: 1 }}
                                >
                                  Endpoint: {job.destination_url}
                                </Typography>
                                <Grid container spacing={3}>
                                  {job.parameters ? (
                                    <ParameterForm
                                      data={job.parameters}
                                      setParameterState={(value) => {
                                        updateJobData(
                                          job.typeId,
                                          "parameters",
                                          value
                                        );
                                      }}
                                    />
                                  ) : (
                                    <></>
                                  )}
                                  {job.validations ? (
                                    <RequiredFieldForm
                                      data={job.validations}
                                      setRequiredState={(value) => {
                                        updateJobData(
                                          job.typeId,
                                          "validations",
                                          value
                                        );
                                      }}
                                    />
                                  ) : (
                                    <></>
                                  )}
                                </Grid>
                                {job.processing_rules ? (
                                  <ProcessingRulesForm
                                    data={job.processing_rules}
                                    setProcessingState={(value) => {
                                      updateJobData(
                                        job.typeId,
                                        "processing_rules",
                                        value
                                      );
                                    }}
                                  />
                                ) : (
                                  <></>
                                )}
                                {/* {job.filters ? ( */}
                                <FilterForm
                                  data={job.filters}
                                  setFilterState={(value) => {
                                    updateJobData(job.typeId, "filters", value);
                                  }}
                                />
                                {/* ) : (
                                  <></>
                                )} */}
                              </MDAccordion>
                            </Box>
                            <IconButton
                              sx={{ color: "error.main" }}
                              onClick={() => handleRemoveItem(job.typeId)}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Box>
                        </div>
                      )}
                    </Draggable>
                  ) : null;
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </MDModal>
    </Box>
  );
}

export default JobFormTesting;
